<template>
    <app-layout v-if="isPageReady">
        <common-loader :isProcessing="isFlowInitializing"></common-loader>
        <common-content-spacer v-if="!isFlowInitializing && isFlowReady" height="medium">
            <div class="propose-time-page">
                <router-link :to="requestRoute" class="propose-time-page__back">
                    ← {{ $t('pages.referrals.pool.propose_page.back') }}
                </router-link>

                <section class="propose-time-page__request">
                    <div class="propose-time-page__who">
                        <span v-if="patientInitials" class="propose-time-page__avatar" aria-hidden="true">{{ patientInitials }}</span>
                        <div>
                            <h1 class="propose-time-page__heading">{{ pageHeading }}</h1>
                            <div v-if="requestSummary" class="propose-time-page__summary">{{ requestSummary }}</div>
                        </div>
                    </div>
                    <dl class="propose-time-page__facts">
                        <div v-if="timeframeLabel">
                            <dt>{{ $t('pages.referrals.pool.propose_page.wants') }}</dt>
                            <dd>{{ timeframeLabel }}</dd>
                        </div>
                        <div v-if="visitTypeLabel">
                            <dt>{{ $t('pages.referrals.pool.propose_page.visit') }}</dt>
                            <dd>{{ visitTypeLabel }}</dd>
                        </div>
                    </dl>
                </section>

                <propose-times-picker
                    :userId="scheduleUser.id"
                    :practiceId="schedulePractice.id"
                    :practiceLocationId="proposePracticeLocationId"
                    :isSending="isSending"
                    @send="sendToPatient"
                ></propose-times-picker>
            </div>
        </common-content-spacer>

        <available-locations-modal
            v-if="showAvailableLocationsModal"
            :referral="referral"
            :locations="locations"
            @go-to-location="onProposeLocationSelected"
            @close="onProposeLocationsModalClose"
        ></available-locations-modal>
        <common-modal v-if="isReasonCodeModalOpen" width="small" @close="closeReasonCodeModal">
            <header class="match-modal-header">
                <h4>{{ reasonConfig.heading }}</h4>
            </header>
            <div class="reason-code-modal__content">
                <p>{{ reasonMessage }}</p>
            </div>
            <div class="match-modal-footer">
                <common-button
                    v-if="reasonConfig.action"
                    :label="reasonConfig.button"
                    type="secondary"
                    size="medium"
                    @click="runReasonAction()"
                ></common-button>
            </div>
        </common-modal>
        <common-modal v-if="isPracticeCreationOpen" width="small" @close="togglePracticeCreationModal">
            <header class="match-modal-header">
                <h4>{{ $t('pages.practice.create.create_practice.heading') }}</h4>
            </header>
            <div class="match-modal-footer">
                <common-button
                    :label="$t('pages.practice.create.create_practice.button')"
                    type="primary"
                    size="large"
                    @click="goToCreatePractice()"
                ></common-button>
            </div>
        </common-modal>
    </app-layout>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import dayjs from 'dayjs'
    // import emitter from 'tiny-emitter/instance'
    import UsersAPI from 'api/UsersAPI'
    import PatientAPI from 'api/PatientAPI'
    import ProposeTimesPicker from 'components/page-elements/referrals/ProposeTimesPicker.vue'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticesAPI from 'api/PracticesAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralPoolProposeFlowMixin from 'mixins/ReferralPoolProposeFlowMixin'
    import AvailableLocationsModal from 'components/page-elements/referrals/AvailableLocationsModal.vue'

    // Calendar month/day navigation updates route query params, which re-runs
    // loadData via beforeRouteUpdate; cache the physician so those navigations
    // don't refetch and re-trigger the full-page loader.
    let physicianCache = null

    export default {
        mixins: [
            PageWithTitleMixin,
            PageWithElementsMixin,
            PageWithDataMixin,
            ReferralsNewPageMixin,
            ScreenSizeDetectionMixin,
            ReferralPoolProposeFlowMixin,
        ],
        components: {
            AppLayout,
            ProposeTimesPicker,
            AvailableLocationsModal,
        },

        data() {
            return {
                physician: {},
                patient: {},
                practice: {},
                selectedSlotsByDate: {},
                sharedState: {
                    totalSlotCount: 0,
                },
                isSending: false,
            }
        },

        provide() {
            return {
                isReferralPool: !this.isReferralRoute(),
                isProposeFlow: true,
                sharedState: this.sharedState,
            }
        },

        loadData(self) {
            if (self.$route.query.pendingInit === '1') {
                return {
                    patient: Promise.resolve({}),
                    physician: Promise.resolve({}),
                    practice: Promise.resolve({}),
                }
            }

            const isFromPatient =
                self.$route.query.isFromPatient === true ||
                self.$route.query.isFromPatient === 'true' ||
                self.$route.query.isFromPatient === '1'
            const validPatientId =
                self.$route.query.patientId &&
                self.$route.query.patientId !== 'null' &&
                self.$route.query.patientId !== 'undefined'
                    ? Number(self.$route.query.patientId)
                    : null

            if (isFromPatient && validPatientId) {
                return {
                    patient: PatientAPI.getPatientDetails(validPatientId, self.$auth.getUser().id),
                    physician: {},
                    practice: {},
                }
            }

            const userId = self.$route.query.userId || self.$auth.getActiveMember()?.id || self.$auth.getUser()?.id
            if (self.$route.query.practiceType === PracticeTypeEnum.PHYSICIAN || userId) {
                return {
                    physician:
                        physicianCache && String(physicianCache.id) === String(userId)
                            ? Promise.resolve(physicianCache)
                            : UsersAPI.get(userId).then((physician) => {
                                  physicianCache = physician
                                  return physician
                              }),
                    patient: {},
                    practice: {},
                }
            } else {
                return {
                    patient: {},
                    physician: {},
                    practice: PracticesAPI.getPracticeById(self.$route.query.practiceId),
                }
            }
        },

        computed: {
            pageTitle() {
                return this.$t('pages.referrals.pool.propose_page.title')
            },

            // The referral this proposal belongs to: where Back goes and where Send returns.
            requestRoute() {
                if (this.isReferralRoute()) {
                    const id = this.referralId()
                    const isPatient = Boolean(this.$route.query.isFromPatient && this.$route.query.isFromPatient !== 'false')
                    return isPatient
                        ? { name: 'referrals-patient.received', params: { id } }
                        : { name: 'referrals.received.show', params: { id } }
                }
                return { name: 'referrals.received.pool.show', params: { id: this.$route.params.poolId } }
            },

            requestPatient() {
                return this.referral?.patient || (this.patient && this.patient.id ? this.patient : null)
            },

            patientName() {
                const p = this.requestPatient
                return p ? [p.firstName, p.lastName].filter(Boolean).join(' ') : ''
            },

            patientInitials() {
                const p = this.requestPatient
                return p ? [p.firstName, p.lastName].map((n) => (n || '').charAt(0).toUpperCase()).join('') : ''
            },

            pageHeading() {
                const count = this.maxSlots || 3
                return this.patientName
                    ? this.$t('pages.referrals.pool.propose_page.heading', { count, name: this.patientName })
                    : this.$t('pages.referrals.pool.propose_page.heading_no_name', { count })
            },

            requestSummary() {
                const diagnosis = typeof this.referral?.diagnosis === 'string' ? this.referral.diagnosis : ''
                const speciality = this.referral?.speciality?.name || ''
                return [diagnosis, speciality].filter(Boolean).join(' · ')
            },

            timeframeLabel() {
                const timeframe = this.referral?.timeframe || this.$route.query.timeFrame
                const label = TimeWindowSearchCriteriaEnum.find((item) => item.value === timeframe)?.label
                return label || this.referral?.customDate || ''
            },

            visitTypeLabel() {
                if (this.referral?.telehealth === true) return this.$t('pages.referrals.pool.propose_page.telehealth')
                if (this.referral?.telehealth === false) return this.$t('pages.referrals.pool.propose_page.in_person')
                return ''
            },

            ...mapGetters('availability', { storeSelectedSlots: 'selectedSlots', maxSlots: 'maxSlots' }),

            scheduleUser() {
                return this.physician && this.physician.id
                    ? this.physician
                    : { id: Number(this.$route.query.userId) || this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id }
            },

            schedulePractice() {
                return this.practice && this.practice.id
                    ? this.practice
                    : { id: Number(this.$route.query.practiceId) || this.$auth.getPractice()?.id }
            },

            proposePracticeLocationId() {
                return Number(this.$route.params.locationId) || 0
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE', 'SET_MAX_SLOTS']),

            syncSelectedSlotsFromStore(slots) {
                const grouped = {}
                slots
                    .slice()
                    .sort((a, b) => dayjs(`${a.date} ${a.startTime}`) - dayjs(`${b.date} ${b.startTime}`))
                    .forEach((slot) => {
                        const entry = {
                            label: `${slot.startTime} - ${slot.endTime}`,
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        }
                        if (!grouped[slot.date]) {
                            this.$set(grouped, slot.date, [])
                        }
                        grouped[slot.date].push(entry)
                    })

                this.selectedSlotsByDate = grouped
                this.updateSlotCount()
            },

            updateSlotCount() {
                this.sharedState.totalSlotCount = Object.values(this.selectedSlotsByDate).reduce(
                    (total, slots) => total + slots.length,
                    0,
                )
            },

            returnToRequest() {
                this.$router.push({ ...this.requestRoute, query: { proposed: '1' } }).catch(() => {})
            },

            sendToPatient() {
                const proposedTimeSlots = []
                Object.values(this.selectedSlotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        proposedTimeSlots.push({
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        })
                    })
                })

                this.isSending = true

                if (this.isReferralRoute()) {
                    const referralId = this.referralId()
                    const data = {
                        slots: proposedTimeSlots,
                    }
                    ReferralsAPI.sendProposedTimeSlots(referralId, data)
                        .then(() => this.returnToRequest())
                        .finally(() => {
                            this.isSending = false
                        })
                    return
                }

                const request = {
                    practiceLocationId: Number(this.$route.params.locationId),
                    proposedTimeSlots: proposedTimeSlots,
                    distance: this.$route.query.distance,
                    userId: this.$auth.getActiveMember().id,
                }
                ReferralsPoolAPI.sendProposeTimeSlots(this.$route.params.poolId, request)
                    .then(() => this.returnToRequest())
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },

        watch: {
            storeSelectedSlots(slots) {
                this.syncSelectedSlotsFromStore(slots)
            },
        },

        mounted() {
            this.SET_MAX_SLOTS(3)
            this.SET_SELECT_MODE(true)
            this.initProposeTimeFlow()
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
        },
    }
</script>
<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .propose-time-page {
        display: flex;
        flex-direction: column;
        gap: rem(16px);
        color: $color--accent-3;

        &__back {
            align-self: flex-start;
            padding: rem(8px) 0;
            color: #0b7fa6;
            font-size: rem(13px);
            font-weight: 500;
            text-decoration: none;

            &:hover {
                color: $color--accent-3;
            }
        }

        &__request {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: rem(12px) rem(32px);
            padding: rem(16px) rem(20px);
            background: #fff;
            border: 1px solid $color--border-base;
            border-radius: rem(12px);
        }

        &__who {
            display: flex;
            align-items: center;
            gap: rem(12px);
            min-width: 0;
        }

        &__avatar {
            flex-shrink: 0;
            width: rem(44px);
            height: rem(44px);
            border-radius: 50%;
            background: $color--accent-5;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
        }

        &__heading {
            margin: 0;
            font-size: rem(18px);
            font-weight: 600;
        }

        &__summary {
            font-size: rem(13px);
            color: #4a5a78;
        }

        &__facts {
            display: flex;
            flex-wrap: wrap;
            gap: rem(8px) rem(24px);
            margin: 0;

            dt {
                font-size: rem(12px);
                color: #4a5a78;
            }

            dd {
                margin: 0;
                font-size: rem(14px);
                font-weight: 600;
            }
        }
    }
</style>
